/**
 * Sprint P20 · Feb 2026 · Cockpit Mine Design Overlay Layer (P1.3).
 *
 * P0 upgrade (Feb 2026): every polyline is now transformed into local scene
 * coordinates via the shared `worldToScene` helper so the geometry sits
 * inside the same local frame as the block model and drillholes. Passing raw
 * UTM points into R3F is the primary cause of the "tiny-dot" bug.
 *
 * Two design kinds are supported:
 *   · engineering_open_pit         — pit crest, toe, ramp helix
 *   · engineering_underground      — decline, trunk drives, crosscuts
 */
import { useMemo } from "react";
import { Line } from "@react-three/drei";
import { worldToScene, isValidCoordinate } from "@/lib/sceneCoords";


export const MINE_DESIGN_COLOURS = {
  pit_crest:      "#22D3EE",  // sky-cyan
  pit_toe:        "#F97316",  // orange
  pit_ramp:       "#FACC15",  // amber
  ug_decline:     "#F472B6",  // pink
  ug_trunk:       "#A78BFA",  // violet
  ug_crosscut:    "#818CF8",  // indigo
};


/**
 * Transform a polyline of [E, N, Elev] triples into an array of local-scene
 * THREE.Vector3 points, skipping any invalid coordinate.
 */
function transformPolyline(points, origin, verticalExaggeration = 1) {
  if (!points?.length || !origin) return [];
  const out = [];
  for (const p of points) {
    if (!p || p.length < 3) continue;
    const [e, n, z] = p;
    if (!isValidCoordinate(e, n, z)) continue;
    out.push(worldToScene(e, n, z, origin, verticalExaggeration));
  }
  return out;
}


/** Render one persisted mine-design overlay layer. */
export default function MineDesignLayer({
  design,
  visibility = true,
  origin,
  verticalExaggeration = 1,
}) {
  const lines = useMemo(() => {
    if (!design || !visibility || !origin) return [];
    const rows = [];
    if (design.kind === "engineering_open_pit") {
      (design.benches || []).forEach((b) => {
        const crest = transformPolyline(b.crest_polyline, origin, verticalExaggeration);
        const toe   = transformPolyline(b.toe_polyline,   origin, verticalExaggeration);
        if (crest.length >= 2) rows.push({
          key: `crest-${b.bench}`, points: crest,
          colour: MINE_DESIGN_COLOURS.pit_crest, width: 2,
        });
        if (toe.length >= 2) rows.push({
          key: `toe-${b.bench}`, points: toe,
          colour: MINE_DESIGN_COLOURS.pit_toe, width: 1.5,
        });
      });
      const ramp = transformPolyline(design.ramp_helix, origin, verticalExaggeration);
      if (ramp.length >= 2) {
        rows.push({ key: "ramp", points: ramp,
                    colour: MINE_DESIGN_COLOURS.pit_ramp, width: 2 });
      }
    } else if (design.kind === "engineering_underground") {
      const decline = transformPolyline(design.decline?.polyline, origin, verticalExaggeration);
      if (decline.length >= 2) {
        rows.push({ key: "decline", points: decline,
                    colour: MINE_DESIGN_COLOURS.ug_decline, width: 3 });
      }
      (design.levels || []).forEach((lvl) => {
        const trunk = transformPolyline(lvl.trunk_drive, origin, verticalExaggeration);
        if (trunk.length >= 2) {
          rows.push({ key: `trunk-${lvl.level}`, points: trunk,
                      colour: MINE_DESIGN_COLOURS.ug_trunk, width: 2 });
        }
        (lvl.crosscuts || []).forEach((cc, i) => {
          const cross = transformPolyline(cc, origin, verticalExaggeration);
          if (cross.length >= 2) {
            rows.push({ key: `cc-${lvl.level}-${i}`, points: cross,
                        colour: MINE_DESIGN_COLOURS.ug_crosscut, width: 1 });
          }
        });
      });
    }
    return rows;
  }, [design, visibility, origin, verticalExaggeration]);

  if (!visibility) return null;
  return (
    <group name="mine-design-overlay">
      {lines.map((l) => (
        <Line key={l.key} points={l.points} color={l.colour}
              lineWidth={l.width} transparent opacity={0.95} />
      ))}
    </group>
  );
}


/** Compute an axis-aligned world-space bounding box from design geometry. */
export function computeMineDesignBBox(design) {
  if (!design) return null;
  let xmin = +Infinity, xmax = -Infinity;
  let ymin = +Infinity, ymax = -Infinity;
  let zmin = +Infinity, zmax = -Infinity;
  const push = (p) => {
    if (!p) return;
    xmin = Math.min(xmin, p[0]); xmax = Math.max(xmax, p[0]);
    ymin = Math.min(ymin, p[1]); ymax = Math.max(ymax, p[1]);
    zmin = Math.min(zmin, p[2]); zmax = Math.max(zmax, p[2]);
  };
  if (design.kind === "engineering_open_pit") {
    (design.benches || []).forEach((b) => {
      (b.crest_polyline || []).forEach(push);
      (b.toe_polyline   || []).forEach(push);
    });
    (design.ramp_helix || []).forEach(push);
  } else if (design.kind === "engineering_underground") {
    (design.decline?.polyline || []).forEach(push);
    (design.levels || []).forEach((lvl) => {
      (lvl.trunk_drive || []).forEach(push);
      (lvl.crosscuts   || []).forEach((cc) => (cc || []).forEach(push));
    });
  }
  if (xmin === +Infinity) return null;
  return {
    xmin, xmax, ymin, ymax, zmin, zmax,
    center: [(xmin + xmax) / 2, (ymin + ymax) / 2, (zmin + zmax) / 2],
    size:   [xmax - xmin, ymax - ymin, zmax - zmin],
  };
}


/** Walk every design vertex as {x=E, y=N, z=Elev} — used by bbox helpers. */
export function mineDesignPointStream(design) {
  const pts = [];
  const push = (p) => {
    if (!p || p.length < 3) return;
    pts.push({ x: p[0], y: p[1], z: p[2] });
  };
  if (!design) return pts;
  if (design.kind === "engineering_open_pit") {
    (design.benches || []).forEach((b) => {
      (b.crest_polyline || []).forEach(push);
      (b.toe_polyline   || []).forEach(push);
    });
    (design.ramp_helix || []).forEach(push);
  } else if (design.kind === "engineering_underground") {
    (design.decline?.polyline || []).forEach(push);
    (design.levels || []).forEach((lvl) => {
      (lvl.trunk_drive || []).forEach(push);
      (lvl.crosscuts   || []).forEach((cc) => (cc || []).forEach(push));
    });
  }
  return pts;
}


export const MINE_DESIGN_LEGEND = [
  { label: "Pit crest",          colour: MINE_DESIGN_COLOURS.pit_crest },
  { label: "Pit toe",            colour: MINE_DESIGN_COLOURS.pit_toe },
  { label: "Haul ramp",          colour: MINE_DESIGN_COLOURS.pit_ramp },
  { label: "Underground decline", colour: MINE_DESIGN_COLOURS.ug_decline },
  { label: "Underground trunk",  colour: MINE_DESIGN_COLOURS.ug_trunk },
  { label: "Crosscut",           colour: MINE_DESIGN_COLOURS.ug_crosscut },
];
